/**
 * Iter 67by — Country-aware hero slider.
 *
 * Replaces the static HERO_BG <img> on Home.jsx. Fetches the deck for the
 * currently-selected country from `/api/public/country-hero-images?country=…`
 * — the backend automatically falls back to the `_default` deck when the
 * country deck is empty, so the hero never goes blank.
 *
 * Cycles every 6s. Pauses on hover. Single image → no auto-rotate, no dots.
 * No images at all → still renders the fallback placeholder image so the
 * homepage layout never collapses.
 */
import { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

const FALLBACK_IMAGE = "https://static.prod-images.emergentagent.com/jobs/ea12290f-d6b9-4b00-8eb3-7f90903e53b3/images/b8e93d4983992acaedd6a901793d226b0e59d0e1c24297ca7013671765a8a106.png";
const AUTO_ADVANCE_MS = 6000;

// Iter 67by/bz — Time-of-day bucket from the renter's browser local clock.
// Day window 06:00-17:59, Night 18:00-05:59. Backend uses this to bias the
// deck toward, e.g., golden-hour shots in the evening when admins have
// tagged images with time_of_day.
function _currentTimeOfDay() {
  const h = new Date().getHours();
  return h >= 6 && h < 18 ? "day" : "night";
}

function _resolveSrc(path) {
  if (!path) return null;
  return /^https?:\/\//i.test(path) ? path : fileUrl(path);
}

export default function HeroSlider({ country }) {
  const [images, setImages] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  // Fetch deck whenever the country changes. Always pass current time-of-day
  // so the backend can bias toward day/night-tagged images (Iter 67bz).
  useEffect(() => {
    let alive = true;
    setIndex(0);
    const params = { time_of_day: _currentTimeOfDay() };
    if (country) params.country = country;
    api.get("/public/country-hero-images", { params })
      .then((r) => { if (alive) setImages(r.data?.images || []); })
      .catch(() => { if (alive) setImages([]); });
    return () => { alive = false; };
  }, [country]);

  // Auto-advance when more than one image and not paused.
  useEffect(() => {
    if (images.length <= 1 || paused) return undefined;
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % images.length);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(timerRef.current);
  }, [images.length, paused]);

  const hasImages = images.length > 0;
  const current = hasImages ? images[index] : null;
  const src = current ? _resolveSrc(current.image_path) : FALLBACK_IMAGE;

  const goPrev = () => setIndex((i) => (i - 1 + images.length) % images.length);
  const goNext = () => setIndex((i) => (i + 1) % images.length);

  return (
    <div
      className="mt-12 relative aspect-[16/7] rounded-3xl overflow-hidden bg-neutral-100 group"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="hero-slider"
    >
      {/* Stacked images with cross-fade transition. Only the active one is
          opaque; others sit at opacity-0 to keep DOM stable for accessibility
          and to prevent layout shift between slides. */}
      {(hasImages ? images : [{ id: "fallback", image_path: FALLBACK_IMAGE, caption: null }]).map((img, i) => {
        const isActive = hasImages ? i === index : true;
        return (
          <img
            key={img.id || i}
            src={_resolveSrc(img.image_path) || FALLBACK_IMAGE}
            alt={img.caption || "Hero"}
            loading={i === 0 ? "eager" : "lazy"}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${isActive ? "opacity-100" : "opacity-0"}`}
            data-testid={`hero-slide-${i}`}
          />
        );
      })}

      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none"></div>

      {/* Caption pill (when set) — bottom-left, glassy. */}
      {current?.caption && (
        <div
          className="absolute bottom-4 left-4 max-w-md bg-black/40 backdrop-blur-md text-white text-sm font-medium px-3 py-1.5 rounded-full ring-1 ring-white/20"
          data-testid="hero-slide-caption"
        >
          {current.caption}
        </div>
      )}

      {images.length > 1 && (
        <>
          {/* Prev / Next chevrons — hidden by default, appear on hover. */}
          <button
            type="button"
            onClick={goPrev}
            aria-label="Previous slide"
            className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/35 hover:bg-black/55 backdrop-blur text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid="hero-slider-prev"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={goNext}
            aria-label="Next slide"
            className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/35 hover:bg-black/55 backdrop-blur text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid="hero-slider-next"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          {/* Dot pagination. */}
          <div
            className="absolute bottom-4 right-4 flex items-center gap-1.5 bg-black/30 backdrop-blur-md rounded-full px-2.5 py-1.5 ring-1 ring-white/20"
            data-testid="hero-slider-dots"
          >
            {images.map((img, i) => (
              <button
                key={img.id || i}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === index ? "w-6 bg-amber-400" : "w-1.5 bg-white/50 hover:bg-white/80"}`}
                data-testid={`hero-slider-dot-${i}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
